'use client';

import {
  QueryCache,
  QueryClient,
  QueryClientProvider,
  MutationCache,
} from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { FetchError } from '@/lib/fetchError';
import { signInOrOutHere } from '@/lib/auth/thisTab';

/** Paths where a 401 must NOT trigger the global sign-out + redirect (no-session / sign-in pages). */
const NO_REDIRECT_PATHS = ['/login', '/signup', '/landing', '/auth'];

// A FetchError (lib/fetchError) carries its status. Most queryFns still throw a bare `new Error(errorData.error)`,
// losing `.status` — for those, fall back to the message.
function is401(error: unknown): boolean {
  if (error instanceof FetchError) return error.status === 401;
  if (!error) return false;
  if (typeof error !== 'object') return false;
  const candidate = error as { status?: number; response?: { status?: number } };
  if (candidate.status === 401) return true;
  if (candidate.response?.status === 401) return true;
  const message = (error as Error).message ?? '';
  return /\b401\b|unauthorized/i.test(message);
}

/** Mirrors `is401` for 429s — they back off per the server's `Retry-After` hint. */
function is429(error: unknown): boolean {
  if (error instanceof FetchError) return error.status === 429;
  if (!error) return false;
  if (typeof error !== 'object') return false;
  const candidate = error as { status?: number; response?: { status?: number } };
  if (candidate.status === 429) return true;
  if (candidate.response?.status === 429) return true;
  const message = (error as Error).message ?? '';
  return /\b429\b|too many requests/i.test(message);
}

/** Parse a `Retry-After` hint from `.retryAfterMs` or `.response.headers`; null if absent. */
function retryAfterMs(error: unknown): number | null {
  if (error instanceof FetchError) return error.retryAfterMs ?? null;
  if (!error || typeof error !== 'object') return null;
  const candidate = error as {
    retryAfterMs?: number;
    response?: { headers?: { get?: (name: string) => string | null } };
  };
  if (typeof candidate.retryAfterMs === 'number') return candidate.retryAfterMs;
  const header = candidate.response?.headers?.get?.('Retry-After');
  if (!header) return null;
  const seconds = Number(header);
  return Number.isFinite(seconds) ? seconds * 1000 : null;
}

/** Shared retryDelay — honors a `Retry-After` hint when present, else capped exp backoff. */
function retryDelayFn(attemptIndex: number, error: unknown): number {
  const hint = retryAfterMs(error);
  if (hint !== null) return hint;
  return Math.min(1000 * 2 ** attemptIndex, 30_000);
}

/**
 * On any 401, the server session is revoked (sign-out / ban / concurrent-session kick): clear
 * client state and full-page redirect to /login so no component renders against a stale user.
 * `scope: 'local'` (server session already gone; 'global' would kill other devices' sessions).
 * `redirectInFlight` dedups concurrent 401s from parallel queries.
 */
let redirectInFlight = false;

async function handleAuthError(error: unknown) {
  // 429s bubble through but need no auth handling — retryDelay honors Retry-After; just log.
  if (is429(error)) {
    const hintMs = retryAfterMs(error);
    console.warn(
      `[RateLimit] 429 received${hintMs !== null ? `; backing off ${Math.ceil(hintMs / 1000)}s` : ''}`,
    );
    return;
  }

  if (!is401(error)) return;
  if (typeof window === 'undefined') return;
  if (redirectInFlight) return;

  const currentPath = window.location.pathname;
  if (NO_REDIRECT_PATHS.some(p => currentPath === p || currentPath.startsWith(p + '/'))) {
    return;
  }

  redirectInFlight = true;
  try {
    // Dynamic import: this provider mounts in the root layout on EVERY page
    // (incl. static /terms, /privacy), and this rare kicked-session path is the
    // only supabase use — a static import would put the SDK (~54 KB gz) back
    // into every page's first-load JS.
    const { createClient } = await import('@/lib/supabase/client');
    await signInOrOutHere(() => createClient().auth.signOut({ scope: 'local' }));
  } catch {
    // SignOut can fail on a stale session — swallow; redirecting anyway.
  }
  const redirectTarget = `/login?redirect=${encodeURIComponent(currentPath + window.location.search)}`;
  window.location.href = redirectTarget;
}

export function QueryProvider({ children }: { children: React.ReactNode }) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        queryCache: new QueryCache({
          onError: handleAuthError,
        }),
        mutationCache: new MutationCache({
          onError: handleAuthError,
        }),
        defaultOptions: {
          queries: {
            // Push-driven invalidation (settings-version poll + revalidateTag) is the primary
            // freshness mechanism; staleTime + refetchInterval are safety nets for a failed poll.
            staleTime: 5 * 60 * 1000,   // 5 minutes
            gcTime: 5 * 60 * 1000,      // 5 minutes
            refetchOnWindowFocus: false,
            // Skip retry on 401 (kicked session — redirect fast) and 429 (rate-limit
            // window hasn't rolled over; retrying burns more budget without helping).
            retry: (failureCount, error) => {
              if (is401(error) || is429(error)) return false;
              return failureCount < 3;
            },
            retryDelay: retryDelayFn,
          },
          mutations: {
            retry: (failureCount, error) => {
              if (is401(error) || is429(error)) return false;
              return failureCount < 1;
            },
            retryDelay: retryDelayFn,
          },
        },
      })
  );

  // Cleanup on unmount to prevent memory leaks.
  useEffect(() => {
    return () => {
      queryClient.clear();
    };
  }, [queryClient]);

  // The app's one auth listener (useSession's syncSession), loaded lazily like the sign-out above.
  useEffect(() => {
    let stop: (() => void) | undefined;
    let gone = false;
    void Promise.all([import('@/lib/supabase/client'), import('@/hooks/useSession')]).then(([{ createClient }, { syncSession }]) => {
      if (!gone) stop = syncSession(queryClient, createClient());
    });
    return () => {
      gone = true;
      stop?.();
    };
  }, [queryClient]);

  return (
    <QueryClientProvider client={queryClient}>
      {children}
    </QueryClientProvider>
  );
}
